import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle2, User, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
const testimonials = [{
  quote: "I uploaded six months of texts and the report quietly named three patterns I'd been calling 'just stress.' Uncomfortable, but useful.",
  author: 'Sarah J.',
  role: 'Product designer',
  rating: 5
}, {
  quote: "The Message Rewriter is what I use most days. It helps me say what I mean — without the edge I didn't realize was there.",
  author: 'Marcus D.',
  role: 'Software engineer',
  rating: 5
}, {
  quote: 'As a therapist, I find the reports a useful conversation starter with clients who want to look at their own communication style.',
  author: 'Em W., LMFT',
  role: 'Licensed therapist',
  rating: 5
}, {
  quote: 'Reading the breakdown of a DARVO exchange with my ex was clarifying. Not dramatic — just clear, with real evidence.',
  author: 'Alex R.',
  role: 'Grad student',
  rating: 5
}, {
  quote: "Privacy was my one requirement. Knowing scans aren't tied to my identity is what made me actually willing to upload anything.",
  author: 'Jenna B.',
  role: 'Founder',
  rating: 4
}, {
  quote: "It didn't tell me my friendship was 'toxic.' It told me which specific patterns were repeating and let me decide what to do.",
  author: 'Chris M.',
  role: 'Teacher',
  rating: 5
}];
export function SocialProofSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden border-t border-gray-900" data-id="element-2955">
      {/* Subtle Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-30 pointer-events-none" data-id="element-2956" />

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-2957">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20" data-id="element-2958">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-3 bg-[#0A0A0A] border border-gray-800 rounded-full px-5 py-2.5 mb-8 shadow-[0_0_30px_rgba(0,0,0,0.5)]" data-id="element-2959">
            <div className="flex gap-0.5" data-id="element-2960">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} className={s === 5 ? 'text-[#FFD700]/50' : 'text-[#FFD700] fill-[#FFD700]'} data-id="element-2961" />)}
            </div>
            <div className="h-3 w-px bg-gray-800" data-id="element-2962" />
            <span className="text-gray-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold" data-id="element-2963">
              <span className="text-white" data-id="element-2964">4.9/5</span> · 10,000+ Scans Analyzed
            </span>
            <div className="h-3 w-px bg-gray-800" data-id="element-2965" />
            <ShieldCheck size={14} className="text-[#FF0000]" data-id="element-2966" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl md:text-5xl font-black text-white tracking-tight mb-5 uppercase" data-id="element-2967">
            Field Reports from{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF0000] to-red-500" data-id="element-2968">
              The Front Lines
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-gray-400 font-mono text-sm md:text-base max-w-2xl mx-auto leading-relaxed" data-id="element-2969">
            Notes from people using ToxicDetector to read their own
            conversations more clearly.
          </motion.p>
        </div>

        {/* Carousel Controls */}
        <div className="flex justify-end gap-3 mb-6" data-id="element-2970">
          <button onClick={() => scroll('left')} className="w-10 h-10 rounded-full border border-gray-800 bg-[#0A0A0A] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FF0000] transition-colors" aria-label="Previous testimonials" data-id="element-2971">
            <ChevronLeft size={20} data-id="element-2972" />
          </button>
          <button onClick={() => scroll('right')} className="w-10 h-10 rounded-full border border-gray-800 bg-[#0A0A0A] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FF0000] transition-colors" aria-label="Next testimonials" data-id="element-2973">
            <ChevronRight size={20} data-id="element-2974" />
          </button>
        </div>

        {/* Testimonials Carousel */}
        <div ref={scrollContainerRef} className="flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory hide-scrollbar" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-2975">
          {testimonials.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} className="relative bg-[#0A0A0A] border border-gray-800 rounded-xl p-7 flex flex-col group hover:border-[#FF0000]/40 transition-colors duration-300 min-w-[320px] md:min-w-[400px] snap-start flex-shrink-0" data-id="element-2976">
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-tl-lg" data-id="element-2977" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-tr-lg" data-id="element-2978" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-bl-lg" data-id="element-2979" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-br-lg" data-id="element-2980" />

              {/* Header row */}
              <div className="flex items-center justify-between mb-5" data-id="element-2981">
                <div className="flex gap-0.5" data-id="element-2982">
                  {[...Array(5)].map((_, i) => <Star key={i} size={13} className={i < item.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-gray-800'} data-id="element-2983" />)}
                </div>
                <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded" data-id="element-2984">
                  <CheckCircle2 size={10} className="text-emerald-400" data-id="element-2985" />
                  <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-[0.15em]" data-id="element-2986">
                    Verified
                  </span>
                </div>
              </div>

              {/* Quote */}
              <p className="text-gray-300 leading-relaxed text-sm md:text-[15px] flex-grow mb-6" data-id="element-2987">
                "{item.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 border-t border-gray-800/80 pt-4 mt-auto" data-id="element-2988">
                <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center border border-gray-800 flex-shrink-0" data-id="element-2989">
                  <User size={15} className="text-gray-500" data-id="element-2990" />
                </div>
                <div className="min-w-0" data-id="element-2991">
                  <p className="text-white font-semibold text-sm truncate" data-id="element-2992">
                    {item.author}
                  </p>
                  <p className="text-gray-500 text-[11px] mt-0.5 tracking-wide" data-id="element-2993">
                    {item.role}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
      __html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `
    }} data-id="element-2994" />
    </section>;
}